import { type ReactNode } from 'react';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { ClickType } from '@jsmdg/tracking';
import { Button, ButtonColor } from '@jsmdg/yoshi';
import { DiscountType } from '../../../shared/enums';
import { useBuybox } from '../../hooks/useBuybox';
import { useProduct } from '../../hooks/useProduct';
import { type ProductDetailContext } from '../../types/types';
import { Price } from '../Price';
import styles from './Buybox.module.scss';

type BuyboxCallToActionProps = {
    readonly shouldHide: boolean;
    readonly isCheckoutDisabled: boolean;
    readonly children: ReactNode;
    readonly isDisabled?: boolean;
    readonly handleBuyboxButtonClick: (
        event?: React.MouseEvent<HTMLButtonElement>,
        clickType?: ClickType,
    ) => Promise<void> | void;
};

const BuyboxCallToAction = ({
    children,
    handleBuyboxButtonClick,
    isCheckoutDisabled,
    isDisabled = false,
    shouldHide,
}: BuyboxCallToActionProps): JSX.Element => {
    const { discountType, originalPrice, price } = useProduct();
    const { purchaseType, seatCount } = useBuybox();
    const { redemptionInformation } = useFragmentContext<ProductDetailContext>();

    const hasFriendsDiscount = discountType === DiscountType.FriendDiscount;

    return (
        <div
            className={classNames(
                'position-fixed w-100 px-3x pt-1x pb-2x grid align-items-center',
                styles.callToActionContainer,
                {
                    [styles.hideCallToActionContainer]: shouldHide,
                },
            )}
        >
            <div className="g-col-12 g-col-2xs-3 g-col-xs-6 g-col-sm-7">
                <Price
                    price={price}
                    originalPrice={originalPrice}
                    multiplier={Number(seatCount) || 1}
                    vouchersTotal={redemptionInformation?.vouchersTotal}
                    hasFriendsDiscount={hasFriendsDiscount}
                    isDisabled={isDisabled}
                    purchaseType={purchaseType}
                />
            </div>
            <div className="g-col-12 g-col-2xs-6 g-col-xs-6 g-col-sm-5 mt-1x">
                <Button
                    onClick={async (event: React.MouseEvent<HTMLButtonElement>) =>
                        handleBuyboxButtonClick(event, ClickType.StickyBottom)
                    }
                    disabled={isCheckoutDisabled}
                    className="mt-1x w-100 text-nowrap"
                    color={ButtonColor.Brand}
                    a11yText={children as string}
                >
                    {children}
                </Button>
            </div>
        </div>
    );
};

// @loadable/component has an issue with the named export which we couldn't resolve so far.
// When using named exports it fails on the second render
// Using a default export for now until we found solution.
export default BuyboxCallToAction; // eslint-disable-line import/no-default-export
